import { z } from "zod";
import { idSchema } from "./common.js";

/**
 * Phase 12c — input schemas for the read procedures (sites / panels /
 * circuits / devices / warnings / search). Outputs flow through the
 * domain types directly via tRPC's JSON serializer; only inputs need
 * validation at the network boundary.
 *
 * The schemas live in `@panelgenie/contracts` so the Phase 13 client
 * imports them alongside the typed `AppRouter` — the client builds
 * forms against `siteByIdInputSchema.shape.id` rather than re-deriving
 * the constraint.
 */

export const siteByIdInputSchema = z.object({ id: idSchema });
export type SiteByIdInput = z.infer<typeof siteByIdInputSchema>;

/**
 * Phase 13b — `rooms.list` / `panels.list` per-site list inputs.
 *
 * These are additive 12c-style reads opened at 13b because the
 * site/room/panel browse hierarchy needs them and the 12d slot
 * deferred them ("If Phase 13 needs a dedicated room read, it lands
 * as an additive 12c-style read at that slice").
 *
 * Both procedures take `{ siteId }` and return the per-site
 * aggregates (`Room[]` / `Panel[]`); cross-tenant access surfaces as
 * `NOT_FOUND` through the same `loadOwnedSite` helper the rest of
 * the read surface uses.
 */
export const roomsListInputSchema = z.object({ siteId: idSchema });
export type RoomsListInput = z.infer<typeof roomsListInputSchema>;

export const panelsListInputSchema = z.object({ siteId: idSchema });
export type PanelsListInput = z.infer<typeof panelsListInputSchema>;

/**
 * Phase 13e — `devices.list` per-site list input.
 *
 * Additive 12c-style read opened at 13e because the R-DEVICE-5
 * duplicate-check modal calls `findLikelyDuplicates` against the
 * site's existing devices before commit. The slice carry-forward
 * note from Phase 12d/13b mandates "if Phase 13 needs a dedicated
 * read, it lands as an additive 12c-style read at that slice".
 */
export const devicesListInputSchema = z.object({ siteId: idSchema });
export type DevicesListInput = z.infer<typeof devicesListInputSchema>;

export const panelByIdInputSchema = z.object({ id: idSchema });
export type PanelByIdInput = z.infer<typeof panelByIdInputSchema>;

export const panelContentsInputSchema = z.object({ panelId: idSchema });
export type PanelContentsInput = z.infer<typeof panelContentsInputSchema>;

export const circuitByIdInputSchema = z.object({
  panelId: idSchema,
  circuitId: idSchema,
});
export type CircuitByIdInput = z.infer<typeof circuitByIdInputSchema>;

export const deviceByIdInputSchema = z.object({ id: idSchema });
export type DeviceByIdInput = z.infer<typeof deviceByIdInputSchema>;

/**
 * Warning status filter for `warnings.byPanel`. `"all"` returns the
 * full history regardless of status (mirrors
 * `WarningRepository.historyForPanel`); the three named statuses filter
 * down to just that arm of the Phase 3 reconciler state machine.
 */
export const warningStatusFilterSchema = z.enum([
  "open",
  "dismissed",
  "auto-resolved",
  "all",
]);
export type WarningStatusFilter = z.infer<typeof warningStatusFilterSchema>;

export const warningsByPanelInputSchema = z.object({
  panelId: idSchema,
  status: warningStatusFilterSchema.default("open"),
});
export type WarningsByPanelInput = z.infer<typeof warningsByPanelInputSchema>;

/**
 * Free-text bottom-up search per R-SEARCH-2 / R-SEARCH-3.
 *
 * `query` is the user's typed string. Empty / whitespace-only queries
 * resolve to an empty result inside the domain `search` — the schema
 * permits them so the Phase 13 surface can debounce without an
 * intermediate "type something" branch.
 *
 * `tag` is the optional Phase 5c tag filter (R-SEARCH-4). Combined with
 * a free-text query, results narrow to circuits carrying the tag whose
 * fields also match the query. Tag-only queries are not supported here;
 * pass at least one query character.
 *
 * `kinds` lets surfaces that already know they want "devices only" or
 * "circuits only" filter at the boundary rather than client-side.
 *
 * `limit` caps the response size; the spec target is <250ms p95, and
 * the typical UI pages at ~20 results.
 */
export const searchBottomUpInputSchema = z.object({
  query: z.string().max(256),
  kinds: z
    .array(z.enum(["site", "panel", "room", "circuit", "device"]))
    .optional(),
  tag: z.string().max(64).optional(),
  threshold: z.number().min(0).max(1).optional(),
  limit: z.number().int().positive().max(500).optional(),
});
export type SearchBottomUpInput = z.infer<typeof searchBottomUpInputSchema>;

/**
 * Top-down browse per R-SEARCH-1. Two scopes:
 *
 * - `{ scope: "panel", panelId }` — composes `panelContents` for the
 *   single panel. The Phase 13c panel detail view consumes this.
 * - `{ scope: "site", siteId }` — composes the per-site flattened set
 *   of installations + devices for the Phase 13 "all circuits on this
 *   site" surface.
 *
 * Optional `tag` filters circuits on the result by tag membership
 * (R-SEARCH-4).
 */
export const searchTopDownInputSchema = z.discriminatedUnion("scope", [
  z.object({
    scope: z.literal("panel"),
    panelId: idSchema,
    tag: z.string().max(64).optional(),
  }),
  z.object({
    scope: z.literal("site"),
    siteId: idSchema,
    tag: z.string().max(64).optional(),
  }),
]);
export type SearchTopDownInput = z.infer<typeof searchTopDownInputSchema>;
